// Components/About/Founder.jsx
import React from "react";
import { FiPhone } from "react-icons/fi";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.2 } },
};

const teamMembers = [
  {
    id: 1,
    name: "Jesus Ignacio Gonzalez",
    title: "Co-Founder & Principal",
    image: "/Images/CEO.png",
    phone: "(925) 655-7741",
    imgStyle: { objectPosition: "center 5%", transform: "scale(1.6) translateY(-6%)" },
    bio: [
      "Jesus Ignacio Gonzalez is the Co-Founder and Principal of Ashlar Building Group, a California general construction firm specializing in high-end remodels, ADUs, and ground-up custom construction.",
      "A third-generation contractor, Jesus was raised in construction and brings deep, generational roots in the industry. From an early age, he learned that the true measure of quality is not speed or volume, but whether a project still stands with pride years later. Many of the homes and buildings his father and grandfather built remain part of the communities they served — projects Jesus still drives by today as a reminder of the responsibility that comes with putting your name on your work.",
      "That same long-term mindset defines Ashlar Building Group. Jesus approaches every project with a focus on thoughtful planning, disciplined execution, and clear accountability. With over a decade of experience leading complex residential and commercial construction, he has developed structured systems around estimating, budgeting, scheduling, and project oversight — allowing clients to move through the construction process with clarity and confidence.",
      "Clients value Jesus's direct involvement and steady leadership. He remains closely engaged throughout each project, ensuring communication is clear, expectations are managed, and decisions are documented. His background in construction operations and business consulting brings an added layer of organization and financial discipline that minimizes surprises and protects the client's investment.",
      "Beyond construction, Jesus is a husband and a father of four. He takes pride in showing his children the projects he has worked on, just as his father once did with him. For Jesus, construction is not simply a trade — it is a legacy built for generations, founded to serve clients who value craftsmanship, integrity, and a professional process.",
    ],
  },
  {
    id: 2,
    name: "Mauricio Manriquez",
    title: "Founder & Operations",
    image: "/Images/mauricio.png",
    phone: "(925) 878-3311",
    imgStyle: { objectPosition: "center top" },
    bio: [
      "Mauricio Manriquez is a seasoned construction professional with over 15 years of experience in the industry. Throughout his career, he has worked closely with investors and private clients, helping transform their visions into high-quality, lasting projects.",
      "As the founder and owner of Ashlar Building Group LLC, Mauricio oversees projects from concept to completion. His firm specializes in full-scope construction services, managing every phase from planning and permitting to final finishes. This end-to-end involvement ensures quality control, efficiency, and accountability at every stage.",
      "Prior to establishing Ashlar Building Group, Mauricio successfully owned and operated an electrical contracting company for more than six years. This foundation in skilled trades strengthened his technical expertise and deepened his understanding of building systems, allowing him to approach projects with precision and confidence.",
      "Through consistent performance and strong community trust, Mauricio has grown his business to handle million-dollar construction projects and multimillion-dollar luxury renovations. His portfolio includes prestigious properties in Bel Air Crest, Beverly Hills, Huntington Beach, San Francisco, Palo Alto, and other high-end markets across California.",
      "Mauricio is known for providing investors and homeowners with peace of mind by serving as the primary contractor on each project. By minimizing reliance on third-party contractors, he reduces risk, ensures consistency, and protects his clients' investments from costly delays or quality issues.",
    ],
  },
];

const MemberCard = ({ member }) => (
  <motion.article
    variants={fadeUp}
    className="flex flex-col items-center text-center"
  >
    {/* Circle photo */}
    <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-[#343a40] shadow-md mb-3">
      <img
        src={member.image}
        alt={member.name}
        loading="lazy"
        className="w-full h-full object-cover"
        style={member.imgStyle || { objectPosition: "center top" }}
      />
    </div>

    {/* Name */}
    <h3 className="text-lg md:text-xl font-bold text-[#212529] mb-1">
      {member.name}
    </h3>

    {/* Title */}
    <p className="text-xs uppercase tracking-wider font-semibold text-[#6c757d] mb-3">
      {member.title}
    </p>

    {/* Phone icon */}
    <div className="flex items-center justify-center mb-3">
      <a
        href={`tel:${member.phone.replace(/\D/g, "")}`}
        className="w-9 h-9 rounded-full border border-[#adb5bd] flex items-center justify-center text-[#495057] hover:bg-[#212529] hover:text-white hover:border-[#212529] transition"
        aria-label="Phone"
      >
        <FiPhone size={15} />
      </a>
    </div>

    {/* CTA button */}
    <a
      href={`tel:${member.phone.replace(/\D/g, "")}`}
      className="bg-[#343a40] hover:bg-[#495057] text-white text-xs font-semibold uppercase tracking-wider px-6 py-2.5 rounded-full transition mb-5"
    >
      Get in Touch
    </a>

    {/* Bio */}
    <div className="w-full text-center space-y-3">
      {member.bio.map((para, i) => (
        <p key={i} className="text-sm text-[#495057] leading-relaxed">
          {para}
        </p>
      ))}
    </div>
  </motion.article>
);

const Founder = () => (
  <section id="founder-section" className="bg-white px-6 pt-8 pb-8">
    <div className="max-w-5xl mx-auto">

      {/* Header */}
      <motion.div
        className="text-center mb-6"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <p className="text-[#6c757d] uppercase tracking-wider text-xs md:text-sm font-semibold mb-2">
          Our Leadership Team
        </p>
        <h2 className="text-3xl md:text-4xl font-bold text-[#212529] mb-4">
          Meet the People Behind Ashlar
        </h2>
        <p className="text-[#495057] text-sm md:text-base leading-relaxed max-w-xl mx-auto">
          Our team brings decades of combined expertise, dedication, and passion to every project we undertake.
        </p>
      </motion.div>

      {/* Quote */}
      <motion.div
        className="bg-[#212529] rounded-lg px-8 py-6 mb-10 text-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        variants={fadeUp}
      >
        <p className="text-[#f8f9fa] text-sm md:text-base leading-relaxed italic mb-3">
          "Build wealth with confidence. We provide investors with direct access to decision-makers,
          clear contractual protections, and full visibility into how capital is deployed throughout
          every stage of a project."
        </p>
        <span className="text-[#adb5bd] text-xs uppercase tracking-wider font-semibold">
          — Ashlar Building Group
        </span>
      </motion.div>

      {/* Side-by-side team members */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {teamMembers.map((member) => (
          <MemberCard key={member.id} member={member} />
        ))}
      </motion.div>

    </div>
  </section>
);

export default Founder;